/**
 * React Query hooks for security issues data fetching and mutations.
 */

import { useQuery, useMutation, useQueryClient, keepPreviousData, type UseQueryOptions } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  listSecurityIssues,
  getSecurityIssue,
  getSecurityIssueByDisplayId,
  updateSecurityIssue,
  deleteSecurityIssue,
  getSecurityIssueStats,
  getSeverityTrend,
  parseDisplayId,
  type SecurityIssueFilters,
  type SecurityIssueUpdateData,
} from "@/services/securityIssuesService";
import type { SeverityTrendResponse } from "@/types/security";
import { useApiReady } from "@/hooks/useApi";

/**
 * Hook to fetch a paginated list of security issues.
 */
export function useSecurityIssues(
  filters?: SecurityIssueFilters,
  options?: Omit<UseQueryOptions<any, Error, any, any[]>, "queryKey" | "queryFn">
) {
  const isApiReady = useApiReady();

  return useQuery({
    queryKey: ["security-issues", filters],
    queryFn: () => listSecurityIssues(filters),
    staleTime: 30000,
    placeholderData: keepPreviousData,
    enabled: isApiReady,
    ...options,
  });
}

/**
 * Hook to fetch a single security issue by numeric ID.
 */
export function useSecurityIssue(id: number) {
  const isApiReady = useApiReady();

  return useQuery({
    queryKey: ["security-issue", id],
    queryFn: () => getSecurityIssue(id),
    enabled: id > 0 && isApiReady,
  });
}

/**
 * Hook to fetch a single security issue by display ID (e.g., "SEC-001").
 */
export function useSecurityIssueByDisplayId(displayId: string) {
  const isApiReady = useApiReady();

  return useQuery({
    queryKey: ["security-issue", displayId],
    queryFn: () => getSecurityIssueByDisplayId(displayId),
    enabled: !!displayId && isApiReady,
  });
}

/**
 * Hook to update a security issue.
 */
export function useUpdateSecurityIssue() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: SecurityIssueUpdateData }) =>
      updateSecurityIssue(id, data),
    onSettled: (_, error, variables) => {
      if (error) {
        toast.error("Failed to update issue");
      } else {
        toast.success("Issue updated");
      }
      // Invalidate the specific issue
      queryClient.invalidateQueries({ queryKey: ["security-issue", variables.id] });
      // Invalidate the list
      queryClient.invalidateQueries({ queryKey: ["security-issues"] });
    },
  });
}

/**
 * Hook to update a security issue by display ID.
 */
export function useUpdateSecurityIssueByDisplayId() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ displayId, data }: { displayId: string; data: SecurityIssueUpdateData }) => {
      const id = parseDisplayId(displayId);
      return updateSecurityIssue(id, data);
    },
    onSettled: (_, error, variables) => {
      if (error) {
        toast.error("Failed to update issue");
      } else {
        toast.success("Issue updated");
      }
      // Invalidate the specific issue
      queryClient.invalidateQueries({ queryKey: ["security-issue", variables.displayId] });
      // Invalidate the list
      queryClient.invalidateQueries({ queryKey: ["security-issues"] });
      // Invalidate activities so the History tab picks up the new status change
      queryClient.invalidateQueries({ queryKey: ["issue-activities"] });
    },
  });
}

/**
 * Hook to delete a security issue.
 */
export function useDeleteSecurityIssue() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => deleteSecurityIssue(id),
    onSettled: (_, error) => {
      if (error) {
        toast.error("Failed to delete issue");
      } else {
        toast.success("Issue deleted");
      }
      // Invalidate the list
      queryClient.invalidateQueries({ queryKey: ["security-issues"] });
    },
  });
}

/**
 * Hook to delete a security issue by display ID.
 */
export function useDeleteSecurityIssueByDisplayId() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (displayId: string) => {
      const id = parseDisplayId(displayId);
      return deleteSecurityIssue(id);
    },
    onSettled: (_, error) => {
      if (error) {
        toast.error("Failed to delete issue");
      } else {
        toast.success("Issue deleted");
      }
      // Invalidate the list
      queryClient.invalidateQueries({ queryKey: ["security-issues"] });
    },
  });
}

/**
 * Hook to fetch aggregated statistics for security issues.
 * @param days - Number of days for trend analysis (default: 30)
 */
export function useSecurityIssueStats(
  days: number = 30,
  organizationId?: number,
  options?: Omit<UseQueryOptions<any, Error, any, any[]>, "queryKey" | "queryFn">
) {
  const isApiReady = useApiReady();

  return useQuery({
    queryKey: ["security-issues-stats", days, organizationId],
    queryFn: () => getSecurityIssueStats(days, organizationId),
    staleTime: 60000,
    placeholderData: keepPreviousData,
    enabled: isApiReady,
    ...options,
  });
}

/**
 * Hook to fetch daily severity trend data for Critical and High issues.
 * @param days - Number of days for trend data (default: 30)
 */
export function useSeverityTrend(
  days: number = 30,
  options?: Omit<UseQueryOptions<SeverityTrendResponse, Error, SeverityTrendResponse, any[]>, "queryKey" | "queryFn">
) {
  const isApiReady = useApiReady();

  return useQuery({
    queryKey: ["severity-trend", days],
    queryFn: () => getSeverityTrend(days),
    staleTime: 60000, // 1 minute
    enabled: isApiReady,
    ...options,
  });
}
